import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Loader2, Eye, EyeOff } from "lucide-react";

export default function AdminLogin() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (token) navigate("/admin/dashboard", { replace: true });
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.login(form.email, form.password);
      localStorage.setItem("admin_token", data.token);
      localStorage.setItem("admin_email", data.email);
      localStorage.setItem("admin_is_super", data.is_super_admin ? "1" : "0");
      navigate("/admin/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ fontFamily: "'Public Sans', sans-serif" }} className="min-h-screen bg-[#f0fdf4] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <img src="/favicon.png" alt="DVC" className="w-10 h-10" />
          </div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Admin Panel</h1>
          <p className="text-slate-500 mt-1 text-sm">Dodder Valley Cycling</p>
        </div>

        <div className="bg-white rounded-2xl border border-green-100 shadow-sm p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label className="text-slate-600 font-medium mb-2 block" htmlFor="admin-email">Email</Label>
              <Input
                id="admin-email"
                data-testid="admin-email-input"
                type="email"
                placeholder="admin@example.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="h-12 bg-slate-50 border-slate-200 focus:bg-white focus:border-green-600"
              />
            </div>
            <div>
              <Label className="text-slate-600 font-medium mb-2 block" htmlFor="admin-password">Password</Label>
              <div className="relative">
                <Input
                  id="admin-password"
                  data-testid="admin-password-input"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                  autoComplete="current-password"
                  className="h-12 bg-slate-50 border-slate-200 focus:bg-white focus:border-green-600 pr-11"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  tabIndex={-1}
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              data-testid="admin-login-btn"
              disabled={loading}
              className="w-full h-12 font-semibold rounded-full bg-[#15803d] hover:bg-green-800 text-white shadow-md"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>
        </div>

        <p className="text-center text-slate-400 text-xs mt-6">
          <a href="/" className="hover:text-green-700 transition-colors">Back to booking page</a>
        </p>
      </div>
    </div>
  );
}
